import { ENGLISH, PEOPLE, PERSON_LABEL, present, preterite } from './conjugate';
import { conj as frPresent, FR_ER, FR_IR, FR_RE } from './french';
import { dePresent, DE_VERBS } from './german';
import { itPresent, IT_VERBS } from './italian';
import { ptPresent, PT_VERBS } from './portuguese';
import { DEFAULT_VERBS, TENSE_BY_ID, type GrammarConfig } from './topics';

// ─────────────────────────────────────────────────────────────────────────────
// Verb charts: for one verb in one tense, every person's pronoun and its form
// ("yo → hablo, tú → hablas…"). The Sunken Fortress builds its block, plate and
// crystal puzzles from them, so a class's dungeon drills the verbs and tenses
// the teacher has assigned. All by rule, like the lessons (no AI).
// ─────────────────────────────────────────────────────────────────────────────

export type Chart = {
  verb: string;
  meaning?: string;
  /** "Present tense", "Preterite (past)", … */
  tense: string;
  /** In person order; `who` is what a pronoun hole or tablet shows. */
  pairs: { who: string; form: string }[];
};

const ES_TENSE: Record<keyof typeof DEFAULT_VERBS, string> = {
  present_regular: 'Present tense', present_stem: 'Present (stem changers)', preterite_regular: 'Preterite (past)',
};

/** The charts for one grammar assignment's settings: the teacher's verbs, else the topic's own list. */
export function chartsFor(cfg: GrammarConfig): Chart[] {
  const out: Chart[] = [];
  const people = PEOPLE.filter(p => cfg.vosotros || p !== 'vosotros');
  for (const t of cfg.topics) {
    if (t === 'present_regular' || t === 'present_stem' || t === 'preterite_regular') {
      const form = t === 'preterite_regular' ? preterite : present;
      const verbs = cfg.verbs?.[t]?.length ? cfg.verbs[t]! : DEFAULT_VERBS[t];
      for (const verb of verbs) {
        out.push({ verb, meaning: ENGLISH[verb], tense: ES_TENSE[t], pairs: people.flatMap(p => { const f = form(verb, p); return f ? [{ who: PERSON_LABEL[p].join(' / '), form: f }] : []; }) });
      }
    } else if (TENSE_BY_ID.has(t)) {
      const { L, T } = TENSE_BY_ID.get(t)!;
      const idx = (T.people ?? L.people.map((_, i) => i)).filter(i => cfg.vosotros || i !== L.optional);
      for (const verb of Object.keys(T.verbs)) {
        out.push({
          verb, meaning: T.verbs[verb], tense: T.title,
          pairs: idx.flatMap(i => {
            const f = T.forms(verb, i)?.[0];
            if (!f) return [];
            const base = L.people[i].join(' / ');
            return [{ who: T.who ? T.who(i, f, base) : base, form: f }];
          }),
        });
      }
    } else if (PRESENTS[t]) out.push(...PRESENTS[t](cfg));
  }
  return out.filter(usable);
}

/** The other languages' present-tense topics. */
const PRESENTS: Record<string, (cfg: GrammarConfig) => Chart[]> = {
  fr_er: () => simple(FR_ER, FR_WHO, frPresent),
  fr_ir: () => simple(FR_IR, FR_WHO, frPresent),
  fr_re: () => simple(FR_RE, FR_WHO, frPresent),
  de_present: () => simple(DE_VERBS, ['ich', 'du', 'er / sie / es', 'wir', 'ihr', 'sie (they) / Sie'], dePresent),
  it_present: () => simple(IT_VERBS, ['io', 'tu', 'lui / lei', 'noi', 'voi', 'loro'], itPresent),
  pt_present: cfg => simple(PT_VERBS, ['eu', 'tu', 'você / ele / ela', 'nós', 'vocês / eles / elas'], ptPresent, cfg.vosotros ? [] : [1]),
};
const FR_WHO = ['je', 'tu', 'il / elle / on', 'nous', 'vous', 'ils / elles'];

function simple(bank: Record<string, string>, who: string[], form: (v: string, i: number) => string, skip: number[] = []): Chart[] {
  return Object.keys(bank).map(verb => ({
    verb, meaning: bank[verb], tense: 'Present tense',
    pairs: who.flatMap((w, i) => (skip.includes(i) ? [] : [{ who: w, form: form(verb, i) }])),
  }));
}

/** A chart a puzzle can use: at least three different forms. */
const usable = (c: Chart) => distinct(c).length >= 3;

/** One pair per form (in German "macht" is er AND ihr: only er's is kept), for puzzles that need every answer to differ. */
export function distinct(c: Chart): Chart['pairs'] {
  const seen = new Set<string>();
  return c.pairs.filter(p => (seen.has(p.form) ? false : (seen.add(p.form), true)));
}

/** What to drill when the class hasn't been assigned any verbs yet: the present tense. */
export function defaultCharts(lang: string): Chart[] {
  const base = lang.split('-')[0];
  if (base === 'es') return chartsFor({ topics: ['present_regular'] });
  if (base === 'fr') return chartsFor({ topics: ['fr_er'] });
  if (base === 'de') return chartsFor({ topics: ['de_present'] });
  if (base === 'it') return chartsFor({ topics: ['it_present'] });
  if (base === 'pt') return chartsFor({ topics: ['pt_present'] });
  if (base === 'en') return chartsFor({ topics: ['en_t_present_continuous'] });
  return [];
}
